import React, { useEffect } from 'react';
import Navbar from '@/components/Navbar/Navbar';
import Footer from '@/view/layout/Layout/Footer';
import usePageTitle from '@/components/_hooks/usePageTitle';
import styled from '@emotion/styled';
import type { PolarisRoute } from '@/view/polarisRoutes';
import FocusTheme from '@/components/Focus/FocusTheme';
import { CssBaseline } from '@mui/material';
import { useLocation } from 'react-router-dom';
import ScrollToTopButton from '@/components/_common/ScrollToTopButton';
import { NAVBAR_HEIGHT } from '@/constants/constants';

type LayoutProps = {
  calcTitle?: PolarisRoute['calcTitle'];
  configOptions: { key: string; label: string }[];
  children: React.ReactNode;
};

export default function Layout({ calcTitle, configOptions, children }: LayoutProps) {
  usePageTitle(calcTitle);

  const location = useLocation<{ animateFocus?: boolean; reverseAnimateFocus?: boolean }>();
  const isFocus = location.pathname.startsWith('/focus');
  const animateFocus = location.state?.animateFocus ?? false;
  const reverseAnimateFocus = location.state?.reverseAnimateFocus ?? false;

  useEffect(() => {
    if (animateFocus || reverseAnimateFocus) {
      window.history.replaceState({}, document.title);
    }
  }, [animateFocus, reverseAnimateFocus]);

  const content = (
    <>
      <NavContainer>
        <NormalNavbar fade={isFocus} reverse={reverseAnimateFocus}>
          <Navbar configOptions={configOptions} />
        </NormalNavbar>
        {isFocus &&
          (animateFocus ? (
            <FocusStyles>
              <Navbar configOptions={configOptions} animateFocus={animateFocus} isFocus />
            </FocusStyles>
          ) : (
            <Navbar configOptions={configOptions} isFocus />
          ))}
        {reverseAnimateFocus && (
          <FocusStyles reverse>
            <Navbar configOptions={configOptions} reverseAnimateFocus={reverseAnimateFocus} isFocus />
          </FocusStyles>
        )}
      </NavContainer>
      <Main>{children}</Main>
      <Footer />
      <ScrollToTopButton />
    </>
  );

  if (isFocus) {
    return (
      <FocusTheme>
        <CssBaseline />
        {content}
      </FocusTheme>
    );
  }

  return content;
}

const NavContainer = styled.div`
  position: relative;
  width: 100%;
  height: ${NAVBAR_HEIGHT}px;
  z-index: 1;

  > * {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
  }
`;

const NormalNavbar = styled.div<{ fade: boolean; reverse: boolean }>`
  z-index: ${({ reverse }) => (reverse ? 1001 : 'auto')};
  ${({ reverse, fade }) =>
    reverse
      ? `
        opacity: 0;
        animation: fadeInNormal 1s forwards;
        animation-delay: 0.3s;
      `
      : `
        opacity: ${fade ? 0.5 : 1};
      `}

  @keyframes fadeInNormal {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`;

const Main = styled.main`
  flex-grow: 1;
  width: 100vw;
  min-height: calc(100vh - 180px);
`;

const FocusStyles = styled.div<{ reverse?: boolean }>`
  > * {
    z-index: 1000;
    opacity: ${({ reverse }) => (reverse ? 1 : 0)};
    animation: ${({ reverse }) => (reverse ? 'fadeOutFocus 1s forwards' : 'fadeIn 0.5s forwards')};
    animation-delay: ${({ reverse }) => (reverse ? '0.5s' : '0s')};
  }

  @keyframes fadeIn {
    to {
      opacity: 1;
    }
  }

  @keyframes fadeOutFocus {
    from {
      opacity: 1;
    }
    to {
      opacity: 0;
    }
  }
`;
